import axios, { type AxiosInstance, type AxiosError, type AxiosRequestConfig } from 'axios';
import { ApiError, type ApiErrorEnvelope, type SuccessEnvelope } from './envelope';

const DEFAULT_TIMEOUT_MS = 15_000;

export interface AuthTokenStore {
  get(): string | null;
  set(token: string | null): void;
  clear(): void;
}

const memoryTokenStore: AuthTokenStore = (() => {
  let token: string | null = null;
  return {
    get: () => token,
    set: (next) => {
      token = next;
    },
    clear: () => {
      token = null;
    },
  };
})();

let unauthorizedHandler: (() => void) | null = null;

export function registerUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (!value || typeof value !== 'object') return false;
  return 'message' in value;
}

export function createApiClient(options?: {
  baseURL?: string;
  tokenStore?: AuthTokenStore;
}): AxiosInstance {
  const baseURL =
    options?.baseURL ?? import.meta.env.VITE_API_BASE_URL ?? 'http://127.0.0.1:8000/api';

  const tokenStore = options?.tokenStore ?? memoryTokenStore;

  const instance = axios.create({
    baseURL,
    timeout: DEFAULT_TIMEOUT_MS,
    withCredentials: false,
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  instance.interceptors.request.use((config) => {
    const token = tokenStore.get();
    if (token) {
      config.headers.set?.('Authorization', `Bearer ${token}`);
    }
    const locale =
      (typeof document !== 'undefined' && document.documentElement.lang) ||
      import.meta.env.VITE_DEFAULT_LOCALE ||
      'ar';
    config.headers.set?.('Accept-Language', locale);
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ApiErrorEnvelope>) => {
      // Network / offline / aborted
      if (!error.response) {
        return Promise.reject(
          new ApiError({
            status: 0,
            message: error.message || 'Network error',
            code: 'network_error',
          }),
        );
      }

      const { status } = error.response;
      const body = error.response.data as unknown;

      if (status === 401) {
        tokenStore.clear();
        unauthorizedHandler?.();
      }

      if (isApiErrorEnvelope(body)) {
        if ('errors' in body) {
          return Promise.reject(
            new ApiError({
              status,
              message: body.message,
              errors: body.errors,
            }),
          );
        }
        if ('code' in body) {
          return Promise.reject(
            new ApiError({
              status,
              message: body.message,
              code: body.code,
              details: body.details,
            }),
          );
        }
      }

      return Promise.reject(
        new ApiError({
          status,
          message:
            (body as { message?: string } | undefined)?.message ?? `HTTP ${status} error`,
          code: `http_${status}`,
        }),
      );
    },
  );

  return instance;
}

export const tokenStore: AuthTokenStore = memoryTokenStore;

let _api: AxiosInstance | null = null;
export function api(): AxiosInstance {
  if (!_api) {
    _api = createApiClient();
  }
  return _api;
}

/** Convenience: unwrap `{ data, meta }` to `data` for query hooks. */
export async function apiData<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await api().request<SuccessEnvelope<T>>(config);
  return response.data.data;
}
